import {
  mdiCashFast,
  mdiClipboardTextClockOutline,
  mdiClockAlertOutline,
  mdiClockOutline,
  mdiFaceRecognition,
  mdiHistory,
  mdiImageSearchOutline,
  mdiListStatus,
  mdiLockOutline,
  mdiSwapHorizontal
} from '@mdi/js'
import type { LocationQuery, RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import PATHS from '@/router/paths'
import { DsAttendanceQueueReader, StationAdvancedReader } from '@/router/permissionsGroupByRouter'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import type { Authorizations } from '@/types/authorization'

type FeatureFlags = ReturnType<typeof useFeatureFlags>['flags']

/** Chaves = os `value` históricos das abas (mantidos para o redirect de `?tab=`). */
export type ApprovalsQueueKey =
  | 'handoverApprovals'
  | 'exceptionality'
  | 'attendance'
  | 'checkValidation'
  | 'facialBiometry'
  | 'priorityPayments'

export type ApprovalsQueueGroupKey = 'schedules' | 'attendance'

export interface ApprovalsQueueGroup {
  key: ApprovalsQueueGroupKey
  title: string
  note: string
}

/**
 * Rodapé do card ("3 expiram hoje · mais antigo há 2 dias"). HOJE É TEXTO FIXO,
 * copiado do protótipo, por decisão de produto (2026-09-08): a informação real
 * depende de um endpoint agregado no backend (idade da fila, expirações do dia,
 * última decisão). Quando ele existir, este campo sai do registro e passa a vir
 * do `useApprovalsHubStore`.
 */
export interface ApprovalsQueueFooter {
  text: string
  icon: string
  /** `alert` = prazo em risco (laranja); `default` = informativo. */
  variant: 'default' | 'alert'
}

export interface ApprovalsQueue {
  key: ApprovalsQueueKey
  path: string
  group: ApprovalsQueueGroupKey
  /** Título do card no hub (pode diferir do título da página). */
  cardTitle: string
  cardDescription: string
  /** Título/descrição do header quando a fila está aberta (breadcrumb "Aprovações › ..."). */
  pageTitle: string
  pageDescription: string
  icon: string
  /** Rótulo que acompanha a contagem: "14 aguardando aprovação". */
  counterLabel: string
  /** Flag que liga a fila; ausente = sempre visível. */
  flag?: keyof FeatureFlags
  badge?: string
  footer?: ApprovalsQueueFooter
  testId: string
  /** Quem acessa a rota da fila (e vê o card). Ausente = `StationAdvancedReader`. */
  authorizations?: Authorizations
}

export const APPROVALS_QUEUE_GROUPS: ApprovalsQueueGroup[] = [
  {
    key: 'schedules',
    title: 'Gestão de escalas',
    note: 'Decisões que mudam quem está alocado ou quanto o plantão vale.'
  },
  {
    key: 'attendance',
    title: 'Registro de Plantão',
    note: 'Decisões que transformam o plantão executado em medição válida.'
  }
]

/**
 * Painel (não fila) exposto no hub abaixo dos grupos de fila. Difere das filas
 * no que ele É: conformidade olhada em painel, sem item esperando decisão
 * individual — por isso não tem contagem, rodapé nem `counterLabel`, e ocupa a
 * linha inteira num grupo só dele. A rota continua fora de `/aprovacoes`
 * (`/pos-plantao`): a mudança é só de ponto de entrada na interface.
 */
export type ApprovalsPanelKey = 'postStation'

export interface ApprovalsPanel {
  key: ApprovalsPanelKey
  path: string
  groupTitle: string
  groupNote: string
  title: string
  description: string
  icon: string
  /** Flag que liga o painel; ausente = sempre visível. */
  flag?: keyof FeatureFlags
  testId: string
}

export const APPROVALS_PANELS: ApprovalsPanel[] = [
  {
    key: 'postStation',
    path: PATHS.POST_STATION,
    groupTitle: 'Pós-plantão',
    groupNote:
      'Conformidade do plantão já executado, olhada em painel e não como fila de decisão item a item.',
    title: 'Painel Pós-Plantão',
    description: 'Avaliação de conformidade dos plantões executados',
    icon: mdiListStatus,
    flag: 'enabledPostStation',
    testId: 'approvals-hub-post-station-panel-card'
  }
]

/** Painéis ligados pelas flags, na ordem do registro. */
export const visibleApprovalsPanels = (flags: FeatureFlags): ApprovalsPanel[] =>
  APPROVALS_PANELS.filter((panel) => !panel.flag || flags[panel.flag] === true)

/**
 * Fonte única das filas de aprovação: alimenta os cards do hub, o header das
 * páginas de fila e o redirect dos links antigos `?tab=`. As rotas em
 * `src/router/index.ts` referenciam estas chaves via `meta.approvalsQueue`.
 */
export const APPROVALS_QUEUES: ApprovalsQueue[] = [
  {
    key: 'exceptionality',
    path: PATHS.APPROVALS_EXCEPTIONALITIES,
    group: 'schedules',
    cardTitle: 'Pedidos de excepcionalidade',
    cardDescription: 'Divergências de jornada ou de valor levantadas na escala, com alçadas.',
    pageTitle: 'Pedidos de excepcionalidade',
    pageDescription:
      'Analise, aprove ou recuse solicitações excepcionais vinculadas a escalas e plantões',
    icon: mdiLockOutline,
    counterLabel: 'aguardando decisão',
    // Sem prazo de aprovação no app: só a idade da fila, sem marcador de atraso.
    footer: { text: 'mais antigo há 11 dias', icon: mdiClockOutline, variant: 'default' },
    testId: 'approvals-hub-exceptionality-card'
  },
  {
    key: 'handoverApprovals',
    path: PATHS.APPROVALS_HANDOVERS,
    group: 'schedules',
    cardTitle: 'Trocas pelo aplicativo',
    cardDescription: 'Trocas e repasses de plantão que os médicos solicitaram pelo app.',
    pageTitle: 'Trocas pelo aplicativo',
    pageDescription: 'Avalie e aprove solicitações de trocas e repasses de plantão',
    icon: mdiSwapHorizontal,
    counterLabel: 'aguardando aprovação',
    flag: 'enabledHandoverV2',
    // Trocas têm prazo de aprovação: o rodapé é o único marcado como alerta.
    footer: {
      text: '3 expiram hoje · mais antigo há 2 dias',
      icon: mdiClockAlertOutline,
      variant: 'alert'
    },
    testId: 'approvals-hub-handover-card'
  },
  {
    key: 'attendance',
    path: PATHS.APPROVALS_DS_ATTENDANCE,
    group: 'attendance',
    cardTitle: 'Avaliação de registros',
    cardDescription: 'Folhas de monitoramento e registros vindos de apps externos.',
    pageTitle: 'Avaliação de registros',
    pageDescription: 'Visualize e gerencie os registros de plantão dos colaboradores',
    icon: mdiClipboardTextClockOutline,
    counterLabel: 'aguardando auditoria',
    flag: 'enabledDsAttendancePolicy',
    footer: { text: 'Última decisão há 2 horas', icon: mdiHistory, variant: 'default' },
    testId: 'approvals-hub-attendance-card',
    // O perfil Extrator lê a fila sem nenhum papel na Gestão de Escalas.
    authorizations: DsAttendanceQueueReader
  },
  {
    key: 'priorityPayments',
    path: PATHS.APPROVALS_PRIORITY_PAYMENTS,
    group: 'attendance',
    cardTitle: 'Pagamentos prioritários',
    cardDescription: 'Plantões À vista e com Antecipe que ainda estão sem horário confirmado.',
    pageTitle: 'Pagamentos prioritários',
    pageDescription:
      'Plantões À vista e com Antecipe: quem precisa enviar Registro de Atividade, o que está na auditoria e o que já está resolvido.',
    icon: mdiCashFast,
    counterLabel: 'precisam de registro',
    flag: 'enabledPriorityPayments',
    badge: 'Novo',
    testId: 'approvals-hub-priority-payments-card'
  },
  {
    key: 'checkValidation',
    path: PATHS.APPROVALS_CHECK_VALIDATION,
    group: 'attendance',
    cardTitle: 'Avaliação de Evidências',
    // Q27 — a fila é aposentada quando o backend liga o bloqueio da inserção
    // manual, NÃO no dia do deploy: enquanto isso o card fica exatamente como
    // está hoje. A cópia da aposentadoria mora em `RETIRED_QUEUE_COPY`.
    cardDescription: 'Registros lançados à mão no painel, com a evidência que os sustenta.',
    pageTitle: 'Avaliação de Evidências',
    pageDescription: 'Decida sobre os registros de presença lançados manualmente no painel',
    icon: mdiImageSearchOutline,
    counterLabel: 'em análise',
    flag: 'enabledCheckValidation',
    badge: 'Novo',
    footer: { text: 'Última decisão há 5 horas', icon: mdiHistory, variant: 'default' },
    testId: 'approvals-hub-check-validation-card'
  },
  {
    key: 'facialBiometry',
    path: PATHS.APPROVALS_FACIAL_BIOMETRY,
    group: 'attendance',
    cardTitle: 'Biometria facial',
    cardDescription: 'Validação da identidade do médico no registro do plantão.',
    pageTitle: 'Biometria facial',
    pageDescription: 'Gerencie a validação de biometria facial',
    icon: mdiFaceRecognition,
    counterLabel: 'aguardando validação',
    flag: 'enabledFacialBiometry',
    footer: { text: 'Última decisão há 3 horas', icon: mdiHistory, variant: 'default' },
    testId: 'approvals-hub-facial-biometry-card'
  }
]

export const isApprovalsQueueKey = (value: unknown): value is ApprovalsQueueKey =>
  typeof value === 'string' && APPROVALS_QUEUES.some((queue) => queue.key === value)

export const findApprovalsQueue = (key: ApprovalsQueueKey): ApprovalsQueue =>
  APPROVALS_QUEUES.find((queue) => queue.key === key) as ApprovalsQueue

/** Autorizações da rota/card de uma fila (default: leitor avançado de escala). */
export const approvalsQueueAuthorizations = (queue: ApprovalsQueue): Authorizations =>
  queue.authorizations ?? StationAdvancedReader

/**
 * Q27 — a fila que é aposentada quando o bloqueio da inserção manual liga no
 * backend (`registrationSettings.manualCheckBlockEnabled`). É a ÚNICA fila com
 * aposentadoria hoje; se aparecer uma segunda, isto vira um campo do registro.
 */
export const RETIRED_QUEUE_KEY: ApprovalsQueueKey = 'checkValidation'

/**
 * Cópia do card DEPOIS da aposentadoria. Fica separada do registro de propósito:
 * `manualCheckBlockEnabled` nasce `false` em PROD, e no dia do deploy nada pode
 * mudar de aparência — o badge "Aposentada" e a descrição de somente leitura só
 * entram quando o backend confirma o bloqueio.
 */
export const RETIRED_QUEUE_COPY = {
  badge: 'Aposentada',
  cardDescription: 'Histórico dos registros lançados à mão no painel. Somente leitura.'
} as const

/**
 * Card com a cópia certa para o estado atual da fila. Hoje o hub ESCONDE a fila
 * aposentada (`visibleApprovalsQueues`), então a cópia de aposentadoria só
 * aparece se essa decisão for afrouxada — mas o par badge/descrição já fica
 * amarrado ao mesmo booleano, em vez de hard-coded no registro.
 */
export const approvalsQueueCard = (queue: ApprovalsQueue, isRetired = false): ApprovalsQueue =>
  isRetired && queue.key === RETIRED_QUEUE_KEY ? { ...queue, ...RETIRED_QUEUE_COPY } : queue

export interface ApprovalsQueueVisibilityOptions {
  /**
   * `true` esconde o card da fila aposentada no hub. A rota continua registrada
   * e respondendo (quem tem a URL salva cai na tela com a faixa de aposentada),
   * e o redirect de `?tab=` continua funcionando — some só o card.
   */
  isCheckValidationRetired?: boolean
  /**
   * Quando informado, esconde a fila cuja rota o usuário não acessaria
   * (`approvalsQueueAuthorizations`). Sem ele, o card de uma fila proibida
   * aparecia, o contador errava e o clique devolvia o usuário ao hub — caso do
   * perfil Extrator, que só lê o Registro de Plantão.
   */
  canAccess?: (authorizations: Authorizations) => boolean
}

/** Filas ligadas pelas flags (e acessíveis, com `canAccess`), na ordem do registro. */
export const visibleApprovalsQueues = (
  flags: FeatureFlags,
  options: ApprovalsQueueVisibilityOptions = {}
): ApprovalsQueue[] =>
  APPROVALS_QUEUES.filter(
    (queue) =>
      (!queue.flag || flags[queue.flag] === true) &&
      !(options.isCheckValidationRetired === true && queue.key === RETIRED_QUEUE_KEY) &&
      (!options.canAccess || options.canAccess(approvalsQueueAuthorizations(queue)))
  )

export const queuesInGroup = (
  queues: ApprovalsQueue[],
  group: ApprovalsQueueGroupKey
): ApprovalsQueue[] => queues.filter((queue) => queue.group === group)

/**
 * Link antigo `/aprovacoes?tab=x` → rota da fila, preservando o resto da query
 * (`status`, `page`... que as filas leem da URL). `null` quando não há o que
 * redirecionar: sem `tab`, `tab` desconhecido, fila desligada ou hub desligado
 * (aí a home continua sendo o shell de abas, que consome `?tab=` ele mesmo).
 */
export const resolveApprovalsTabRedirect = (
  query: LocationQuery,
  flags: FeatureFlags
): RouteLocationRaw | null => {
  if (!flags.enabledApprovalsHub) return null
  const { tab, ...rest } = query
  if (!isApprovalsQueueKey(tab)) return null
  const queue = visibleApprovalsQueues(flags).find((candidate) => candidate.key === tab)
  if (!queue) return null
  return { path: queue.path, query: rest, replace: true }
}

/**
 * Guard das rotas de fila: com o hub DESLIGADO a UI nova não deve aparecer, então
 * a rota volta para o shell de abas (`/aprovacoes?tab=<fila>`), preservando a
 * query — simétrico ao redirect de `?tab=` quando o hub está ligado.
 *
 * A flag da própria fila manda nos dois modos: desligada, a URL direta volta
 * para `/aprovacoes` (sem `?tab=`, que apontaria para uma aba inexistente) em
 * vez de abrir a fila que o card e a aba escondem.
 */
export const resolveApprovalsQueueGuard = (
  key: ApprovalsQueueKey,
  to: Pick<RouteLocationNormalized, 'query'>,
  flags: FeatureFlags
): RouteLocationRaw | true => {
  const flag = findApprovalsQueue(key).flag
  if (flag && flags[flag] !== true) return { path: PATHS.APPROVALS, replace: true }
  return flags.enabledApprovalsHub
    ? true
    : { path: PATHS.APPROVALS, query: { ...to.query, tab: key }, replace: true }
}

export interface ApprovalsQueueLocation {
  path: string
  query?: Record<string, string>
}

/**
 * Destino de navegação para uma fila, para quem sai de uma tela de detalhe
 * ("Voltar", "Abrir módulo de origem"): rota da fila com o hub ligado, `?tab=`
 * do shell de abas com ele desligado — sem depender do redirect do `beforeEnter`
 * (que custa um hop e um `replace` no histórico).
 */
export const approvalsQueueLocation = (
  key: ApprovalsQueueKey,
  query: Record<string, string> = {},
  flags: FeatureFlags = useFeatureFlags().flags
): ApprovalsQueueLocation => {
  if (flags.enabledApprovalsHub) {
    const location: ApprovalsQueueLocation = { path: findApprovalsQueue(key).path }
    if (Object.keys(query).length) location.query = query
    return location
  }
  return { path: PATHS.APPROVALS, query: { tab: key, ...query } }
}

export interface ApprovalsBreadcrumb {
  label: string
  route: string
}

/**
 * Breadcrumbs de uma tela de DETALHE dentro de uma fila
 * ("Aprovações › Trocas pelo aplicativo › Avaliação de Troca"): o header mostra
 * os dois níveis como links e o título da tela por último. O link da fila segue
 * a mesma regra de `approvalsQueueLocation` (rota própria × `?tab=` legado).
 */
export const approvalsQueueBreadcrumbs = (
  key: ApprovalsQueueKey,
  flags: FeatureFlags = useFeatureFlags().flags
): ApprovalsBreadcrumb[] => {
  const queue = findApprovalsQueue(key)
  const queueRoute = flags.enabledApprovalsHub ? queue.path : `${PATHS.APPROVALS}?tab=${key}`
  return [
    { label: 'Aprovações', route: PATHS.APPROVALS },
    { label: queue.pageTitle, route: queueRoute }
  ]
}
